/**
 * Componenta principală pentru managementul body parts
 */
import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab
} from '@mui/material';
import {
  BookmarkBorder as NomenclatorIcon,
  Assignment as MappingIcon,
  Assessment as StatsIcon,
  MenuBook as DictionaryIcon,
  CloudSync as ExternalIcon,
  LocalHospital as InvestigationIcon
} from '@mui/icons-material';

import { PageHeader } from '../ui';
import { BodyPartsStats } from './BodyPartsStats';
import { ExternalNomenclatorsTab } from './ExternalNomenclatorsTab';
import ReviewMappingsPage from '../../pages/ReviewMappingsPage';
import { reviewMappingsApi } from '../../services/reviewMappingsApi';
import { VocabularyTabs } from '../Nomenclator/VocabularyTabs';
import { SegmentCatalogRefactored } from '../Nomenclator/SegmentCatalogRefactored';
import { MappingRulesRefactored } from '../Nomenclator/MappingRulesRefactored';
import { DictionaryManager } from '../Dictionary/DictionaryManager';
import InvestigationTypesPage from '../../pages/InvestigationTypesPage';

export const BodyPartsManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  // Încarcă numărul de mapping-uri pending
  React.useEffect(() => {
    const loadPendingCount = async () => {
      try {
        const response = await reviewMappingsApi.getPendingMappings({ 
          status: 'pending',
          limit: 1 
        });
        setPendingCount(response.total);
      } catch (error) {
        console.error('Error loading pending count:', error);
      }
    };

    loadPendingCount();
  }, []);


  const handleNeedRefresh = () => {
    // Switch la tab-ul de review dacă sunt mapping-uri pending
    if (pendingCount && pendingCount > 0) {
      setActiveTab(1); // Review Mapping-uri este acum pe indexul 1
    }
  };

  // Definim tab-urile cu iconițe - clean și standardizat
  const tabs = [
    // Nomenclatoare externe (HIS) - primul pentru vizibilitate
    {
      label: 'Nomenclatoare HIS',
      icon: <ExternalIcon />,
      component: <ExternalNomenclatorsTab />
    },
    // Nomenclator v1 deprecated - folosim doar v2
    // {
    //   label: 'Nomenclator',
    //   icon: <NomenclatorIcon />,
    //   component: <NomenclatorManagement />
    // },
    {
      label: 'Vocabular',
      icon: <NomenclatorIcon />,
      component: <VocabularyTabs />
    },
    {
      label: 'Catalog Segmente',
      icon: <NomenclatorIcon />,
      component: <SegmentCatalogRefactored />
    },
    {
      label: 'Mapping Rules',
      icon: <MappingIcon />,
      component: <MappingRulesRefactored />
    },
    {
      label: 'Dicționar',
      icon: <DictionaryIcon />,
      component: <DictionaryManager />
    },
    {
      label: pendingCount && pendingCount > 0 
        ? `Review Mapping-uri (${pendingCount})` 
        : 'Review Mapping-uri',
      icon: <MappingIcon />,
      component: <ReviewMappingsPage />
    },
    {
      label: 'Statistici',
      icon: <StatsIcon />,
      component: (
        <BodyPartsStats 
          onNeedRefresh={handleNeedRefresh}
        />
      )
    },
    {
      label: 'Investigații',
      icon: <InvestigationIcon />,
      component: <InvestigationTypesPage />
    }
  ];

  // Tab-urile ca element pentru PageHeader - compact styling
  const tabsElement = (
    <Tabs 
      value={activeTab} 
      onChange={(_, newValue: number) => setActiveTab(newValue)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        minHeight: 36,
        '& .MuiTab-root': {
          minHeight: 36,
          py: 0.5,
          px: 1.5,
          fontSize: '0.78rem',
          textTransform: 'none',
          '& .MuiSvgIcon-root': {
            fontSize: '1rem',
            mr: 0.5
          }
        }
      }}
    >
      {tabs.map((tab, index) => (
        <Tab
          key={index}
          icon={tab.icon}
          iconPosition="start"
          label={tab.label}
        />
      ))}
    </Tabs>
  );

  return (
    <Box>
      {/* Unified Page Header cu titlu și tab-uri */}
      <PageHeader
        title="Body Parts Management"
        tabs={tabsElement}
      />

      {/* Content pentru tab-ul activ */}
      {tabs[activeTab]?.component}
    </Box>
  );
};
